import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTheme } from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { NavBar } from 'components/NavBar/NavBar'
import {
  Button,
  Flex,
  MainHeadline,
  PageWrapper,
  TabsContainer,
  Tabs,
} from 'styling/GlobalStyles'
import { CalendarInput } from 'components/CalendarInput/CalendarInput'
import {
  BodyText,
  Heading3,
  OtherText,
  ParagraphText,
  TitleText,
} from 'styling/Typography'
import {
  TopRow,
  TitleBlock,
  TopRight,
  StatusPill,
  Grid4,
  Grid5,
  MetricCard,
  MetricValue,
  Split,
  Panel,
  InfoPairGrid,
  FuelBar,
  FuelFill,
  StateGrid,
  StateTile,
  RegTable,
  ChartBox,
  ChartBars,
  Bar,
  ChartFooter,
  TargetChip,
  OpTable,
  SignalBox,
  ScrollIndicator,
  ScrollFill,
  StackedBar,
  StackedSeg,
  LegendList,
  BenchRow,
  BenchList,
  FuelMetricsPanel,
  FuelTitleRow,
  FuelMetricGrid,
  FuelMetricItem,
  BoundaryNote,
  CostEfficiencyBox,
  CostBarStacked,
  CostTrack,
  CostFill,
  ComingSoonWrapper,
  fleetColors,
} from './MachineDetail.styled'
import {
  clearFleetMachineOverview,
  getFleetMachineOverview,
  IFleetMachineOverview,
} from 'features/machinery/fleetMachinesSlice'

const ArrowLeftIcon = () => (
  <svg width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2.5} strokeLinecap='round' strokeLinejoin='round'>
    <path d='M15 18l-6-6 6-6' />
  </svg>
)

const UserIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2} strokeLinecap='round' strokeLinejoin='round'>
    <circle cx='12' cy='8' r='4' />
    <path d='M4 21a8 8 0 0116 0' />
  </svg>
)

const PinIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2} strokeLinecap='round' strokeLinejoin='round'>
    <path d='M12 22s-7-7.58-7-13a7 7 0 1114 0c0 5.42-7 13-7 13z' />
    <circle cx='12' cy='9' r='2.5' />
  </svg>
)

const FuelIconSvg = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2} strokeLinecap='round' strokeLinejoin='round'>
    <path d='M4 21h9V5a2 2 0 00-2-2H6a2 2 0 00-2 2v16z' />
    <path d='M13 9h3l2 2v7a2 2 0 01-2 2' />
  </svg>
)

const TAB_LIST = ['Overview', 'Performance', 'Cost', 'Telemetry', 'Maintenance', 'Status Log'] as const
type Tab = (typeof TAB_LIST)[number]

const operationalStatusMap: Record<number, { label: string; tone: 'success' | 'warning' | 'danger' }> = {
  1: { label: 'Idle', tone: 'warning' },
  2: { label: 'Running', tone: 'success' },
}

const availabilityStatusMap: Record<number, { label: string; tone: 'success' | 'warning' | 'danger' }> = {
  1: { label: 'Available', tone: 'success' },
  2: { label: 'In Use', tone: 'warning' },
}

const healthStatusMap: Record<number, { label: string; tone: 'success' | 'warning' | 'danger' }> = {
  1: { label: 'Healthy', tone: 'success' },
  2: { label: 'Broken', tone: 'danger' },
  3: { label: 'In Maintenance', tone: 'warning' },
}

const ownershipMap: Record<number, string> = {
  1: 'Owned',
  2: 'Leased',
}

const ComingSoon = ({ children }: { children: React.ReactNode }) => (
  <ComingSoonWrapper>
    <div className='cs-blur'>{children}</div>
    <div className='cs-label'>Coming Soon</div>
  </ComingSoonWrapper>
)

export const MachineDetail: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const theme: any = useTheme()
  const dispatch = useDispatch<AppDispatch>()
  const [tab, setTab] = useState<Tab>('Overview')
  const [dateRange, setDateRange] = useState<{ firstDay: Date; lastDay: Date } | null>(null)

  const overview = useSelector((state: RootState) => state.fleetMachines.overview)

  useEffect(() => {
    if (id) dispatch(getFleetMachineOverview(id))
    return () => { dispatch(clearFleetMachineOverview()) }
  }, [id])

  const opStatus = overview ? operationalStatusMap[overview.operationalStatus] : null
  const avStatus = overview ? availabilityStatusMap[overview.availabilityStatus] : null
  const hlStatus = overview ? healthStatusMap[overview.healthStatus] : null

  return (
    <>
      <NavBar />
      <PageWrapper noFooter>
        <TopRow>
          <TitleBlock>
            <button className='back' onClick={() => navigate(-1)} aria-label='back'>
              <ArrowLeftIcon />
            </button>
            <MainHeadline style={{ textTransform: 'none', color: theme.textColor }}>
              {overview?.name ?? 'N/A'}
            </MainHeadline>
            <TitleText grey style={{ margin: 0 }}>
              {overview?.machineModel?.name ?? ''}
            </TitleText>
          </TitleBlock>
          <TopRight>
            {tab === 'Performance' ? (
              <>
                <CalendarInput
                  range
                  onChange={(firstDay, lastDay) => setDateRange({ firstDay, lastDay })}
                  defaultValue={dateRange ? { firstDay: dateRange.firstDay, lastDay: dateRange.lastDay } : undefined}
                />
                <Button orange onClick={() => navigate(`/machinery/${overview?.machineCategory?.id}/${id}`)}>Edit</Button>
              </>
            ) : (
              <>
                {opStatus && <StatusPill tone={opStatus.tone}>{opStatus.label}</StatusPill>}
                {avStatus && <StatusPill tone={avStatus.tone}>{avStatus.label}</StatusPill>}
                {hlStatus && <StatusPill tone={hlStatus.tone}>{hlStatus.label}</StatusPill>}
                <Button orange onClick={() => navigate(`/machinery/${overview?.machineCategory?.id}/${id}`)}>Edit</Button>
              </>
            )}
          </TopRight>
        </TopRow>

        <TabsContainer style={{ marginBottom: '1rem', width: 'fit-content' }}>
          {TAB_LIST.map((t) => (
            <Tabs key={t} active={tab === t} onClick={() => setTab(t)}>
              {t}
            </Tabs>
          ))}
        </TabsContainer>

        {tab === 'Overview' && <OverviewTab overview={overview} />}
        {tab === 'Performance' && (
          <ComingSoonWrapper>
            <div className='cs-blur'><PerformanceTab /></div>
            <div className='cs-label'>Coming Soon</div>
          </ComingSoonWrapper>
        )}
        {tab !== 'Overview' && tab !== 'Performance' && (
          <Panel>
            <Flex center style={{ justifyContent: 'center', minHeight: 240 }}>
              <BodyText grey>{tab} view coming soon</BodyText>
            </Flex>
          </Panel>
        )}
      </PageWrapper>
    </>
  )
}

const OverviewTab = ({ overview }: { overview: IFleetMachineOverview | null }) => {
  const theme: any = useTheme()

  const labelStyle = {
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    margin: 0,
  }

  const formatCurrency = (v: number) =>
    v != null ? `$${v.toLocaleString()}` : 'N/A'

  const formatDate = (iso: string) => {
    if (!iso) return 'N/A'
    return new Date(iso).toISOString().split('T')[0]
  }

  const operatorName = overview?.operator
    ? `${overview.operator.firstName} ${overview.operator.lastName}`
    : 'N/A'

  return (
    <>
      <Grid4>
        <MetricCard accent={theme.colorOrange}>
          <ParagraphText grey weight='600' style={labelStyle}>Total Utilization</ParagraphText>
          <MetricValue accent={theme.colorOrange}>
            {overview?.utilizationPercentage != null ? `${overview.utilizationPercentage}%` : 'N/A'}
          </MetricValue>
          <OtherText grey style={{ margin: 0 }}>last 30 days</OtherText>
        </MetricCard>

        <MetricCard accent={theme.textColor}>
          <ParagraphText grey weight='600' style={labelStyle}>Operating Hours</ParagraphText>
          <MetricValue accent={theme.textColor}>
            {overview?.operatingHoursLast30Days != null ? `${overview.operatingHoursLast30Days}h` : 'N/A'}
          </MetricValue>
          <OtherText grey style={{ margin: 0 }}>last 30 days</OtherText>
        </MetricCard>

        <ComingSoon>
          <MetricCard accent={theme.textColor}>
            <ParagraphText grey weight='600' style={labelStyle}>MTBF</ParagraphText>
            <MetricValue accent={theme.textColor}>—</MetricValue>
            <OtherText grey style={{ margin: 0 }}>last 30 days</OtherText>
          </MetricCard>
        </ComingSoon>

        <MetricCard accent={theme.textColor}>
          <ParagraphText grey weight='600' style={labelStyle}>Cost/Hour</ParagraphText>
          <MetricValue accent={theme.textColor}>
            {overview?.costPerHour != null ? formatCurrency(overview.costPerHour) : 'N/A'}
          </MetricValue>
          <OtherText grey style={{ margin: 0 }}>all-in</OtherText>
        </MetricCard>
      </Grid4>

      <Split cols='3fr 2fr'>
        <Panel>
          <Heading3 weight='700' style={{ margin: '0 0 1rem', fontSize: 18 }}>Live Status</Heading3>
          <InfoPairGrid>
            <div>
              <ParagraphText grey weight='600' style={labelStyle}>Operator</ParagraphText>
              <div className='col-value' style={{ marginTop: 6 }}>
                <UserIcon />
                <BodyText weight='600' style={{ margin: 0 }}>{operatorName}</BodyText>
              </div>
            </div>
            <div>
              <ParagraphText grey weight='600' style={labelStyle}>Location</ParagraphText>
              <div className='col-value' style={{ marginTop: 6 }}>
                <PinIcon />
                <BodyText weight='600' style={{ margin: 0 }}>{overview?.location?.name ?? 'N/A'}</BodyText>
              </div>
            </div>
            <div>
              <ParagraphText grey weight='600' style={labelStyle}>Fuel Level</ParagraphText>
              <BodyText weight='600' style={{ margin: '6px 0 0' }}>
                {overview?.fuelPercentage != null ? `${overview.fuelPercentage}%` : 'N/A'}
              </BodyText>
              <FuelBar>
                <FuelFill width={overview?.fuelPercentage ?? 0} />
              </FuelBar>
            </div>
            <ComingSoon>
              <div>
                <ParagraphText grey weight='600' style={labelStyle}>Last Telemetry</ParagraphText>
                <BodyText className='telemetry' weight='600' style={{ margin: '6px 0 0', color: theme.colorGreen }}>● — ago</BodyText>
              </div>
            </ComingSoon>
          </InfoPairGrid>

          <div style={{ borderTop: `1px solid ${fleetColors.divider}`, paddingTop: '0.75rem' }}>
            <ParagraphText grey weight='600' style={labelStyle}>State Dimensions</ParagraphText>
            <StateGrid>
              {[
                {
                  l: 'Operational',
                  v: overview ? (operationalStatusMap[overview.operationalStatus]?.label ?? 'N/A') : 'N/A',
                  s: 'src: Telemetry',
                },
                {
                  l: 'Availability',
                  v: overview ? (availabilityStatusMap[overview.availabilityStatus]?.label ?? 'N/A') : 'N/A',
                  s: 'src: Scheduler',
                },
                {
                  l: 'Health',
                  v: overview ? (healthStatusMap[overview.healthStatus]?.label ?? 'N/A') : 'N/A',
                  s: 'src: Maintenance',
                },
              ].map((t) => (
                <StateTile key={t.l}>
                  <ParagraphText grey weight='600' style={labelStyle}>{t.l}</ParagraphText>
                  <TitleText weight='700' style={{ margin: '8px 0 4px' }}>{t.v}</TitleText>
                  <OtherText grey style={{ margin: 0 }}>{t.s}</OtherText>
                </StateTile>
              ))}
            </StateGrid>
          </div>
        </Panel>

        <Panel>
          <Heading3 weight='700' style={{ margin: '0 0 1rem', fontSize: 18 }}>Identity &amp; Registration</Heading3>
          <RegTable>
            {[
              ['Asset ID', overview?.id != null ? `M-${String(overview.id).padStart(4, '0')}` : 'N/A'],
              ['Category', overview?.machineCategory?.name ?? 'N/A'],
              ['Model', overview?.machineModel?.name ?? 'N/A'],
              ['Year', overview?.yearOfManufacture != null ? String(overview.yearOfManufacture) : 'N/A'],
              ['Chassis #', overview?.chassisNumber ?? 'N/A'],
              ['License Plate', overview?.licensePlate ?? 'N/A'],
              ['Ownership', overview?.ownership != null ? (ownershipMap[overview.ownership] ?? 'N/A') : 'N/A'],
              ['Acquired', overview?.purchaseDate ? formatDate(overview.purchaseDate) : 'N/A'],
              ['Acquisition Cost', overview?.machineCost != null ? formatCurrency(overview.machineCost) : 'N/A'],
            ].map(([k, v]) => (
              <div className='row' key={k}>
                <BodyText grey style={{ margin: 0 }}>{k}</BodyText>
                <BodyText weight='600' style={{ margin: 0 }}>{v}</BodyText>
              </div>
            ))}
          </RegTable>
        </Panel>
      </Split>
    </>
  )
}

const utilBars = [72, 55, 88, 60, 42, 78, 95, 80, 33, 58, 66, 90, 44, 70, 62, 82, 50, 74, 48, 86, 68, 54, 92, 58, 76, 40, 84, 70, 88, 64]
const opHoursBars = [8, 10, 12, 9, 11, 13, 14, 10, 7, 9, 11, 12, 10, 13, 8, 11, 14, 10, 9, 12, 13, 11, 14, 10, 12, 9, 13, 11, 14, 12]

const operators = [
  { name: 'Sarah Chen', shifts: 12, op: '94.2', idle: '7.5', util: 81, idlePct: 8 },
  { name: 'James Mitchell', shifts: 14, op: '108.5', idle: '12', util: 78, idlePct: 11 },
  { name: 'David Okafor', shifts: 6, op: '47.5', idle: '5.7', util: 74, idlePct: 12 },
  { name: 'Marcus Owen', shifts: 8, op: '62.3', idle: '11.2', util: 69, idlePct: 18, incident: true },
]

const bench = [
  { k: 'Availability', num: '87.2%', vs: 'vs 82%', delta: '+6.3%', positive: true },
  { k: 'Utilization', num: '71.4%', vs: 'vs 68.5%', delta: '+4.2%', positive: true },
  { k: 'MTBF', num: '218h', vs: 'vs 198h', delta: '+10.1%', positive: true },
  { k: 'MTTR', num: '3.8h', vs: 'vs 4.5h', delta: '-15.6%', positive: true },
  { k: 'Idle time', num: '13.4%', vs: 'vs 14.8%', delta: '-9.5%', positive: true },
  { k: 'Cost/op-hour', num: '$186', vs: 'vs $203', delta: '-8.4%', positive: true },
]

const PerformanceTab = () => {
  const theme: any = useTheme()

  const labelStyle = {
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    margin: 0,
  }

  const downtime = [
    { name: 'Scheduled Maintenance', hrs: '14.5h', pct: 34, color: fleetColors.info },
    { name: 'Unplanned breakdown', hrs: '11.2h', pct: 26, color: fleetColors.danger },
    { name: 'Awaiting operator', hrs: '6.8h', pct: 16, color: fleetColors.yellow },
    { name: 'Refueling', hrs: '4.9h', pct: 12, color: theme.colorOrange },
    { name: 'Shift change', hrs: '3.2h', pct: 8, color: fleetColors.warning },
    { name: 'Weather', hrs: '1.7h', pct: 4, color: fleetColors.purple },
  ]

  const metrics = [
    { label: 'Availability', value: '87.2%', accent: theme.colorGreen, delta: '↗ +2.1', deltaColor: theme.colorGreen, sub: 'vs 82% fleet avg' },
    { label: 'Utilization', value: '71.4%', accent: theme.colorOrange, delta: '↘ -3.6', deltaColor: theme.colorRed, sub: 'target 75%' },
    { label: 'Operating Hours', value: '312.5', accent: theme.textColor, sub: 'out of 720 calendar' },
    { label: 'Idle Time', value: '48.2h', accent: fleetColors.warning, sub: '13.4% of op time' },
    { label: 'MTBF', value: '218h', accent: theme.colorGreen, delta: '↗ +10%', deltaColor: theme.colorGreen, sub: 'vs 198h fleet avg' },
    { label: 'MTTR', value: '3.8h', accent: theme.textColor, sub: 'per incident' },
    { label: 'Engine Hours', value: '360.7', accent: theme.textColor, sub: 'cumulative +360.7h' },
    { label: 'Downtime', value: '42.3h', accent: theme.colorOrange, sub: '12.8% of ops time' },
    { label: 'Total Cost', value: '$58.2k', accent: theme.colorOrange, sub: '30-day rollup' },
    { label: 'Cost / Op-Hour', value: '$186', accent: theme.textColor, delta: '↘ -8%', deltaColor: theme.colorGreen, sub: 'vs $203 fleet avg' },
  ]

  return (
    <>
      <Grid5>
        {metrics.slice(0, 5).map((m) => (
          <MetricCard key={m.label} accent={m.accent}>
            <ParagraphText grey weight='600' style={labelStyle}>{m.label}</ParagraphText>
            <MetricValue accent={m.accent}>{m.value}</MetricValue>
            {m.delta ? (
              <OtherText style={{ margin: 0, color: m.deltaColor, fontWeight: 600 }}>
                {m.delta} <span style={{ color: '#8A8A8A', fontWeight: 400 }}>{m.sub}</span>
              </OtherText>
            ) : (
              <OtherText grey style={{ margin: 0 }}>{m.sub}</OtherText>
            )}
          </MetricCard>
        ))}
      </Grid5>

      <Grid5>
        {metrics.slice(5).map((m) => (
          <MetricCard key={m.label} accent={m.accent}>
            <ParagraphText grey weight='600' style={labelStyle}>{m.label}</ParagraphText>
            <MetricValue accent={m.accent}>{m.value}</MetricValue>
            {m.delta ? (
              <OtherText style={{ margin: 0, color: m.deltaColor, fontWeight: 600 }}>
                {m.delta} <span style={{ color: '#8A8A8A', fontWeight: 400 }}>{m.sub}</span>
              </OtherText>
            ) : (
              <OtherText grey style={{ margin: 0 }}>{m.sub}</OtherText>
            )}
          </MetricCard>
        ))}
      </Grid5>

      <Split cols='1fr 1fr'>
        <Panel>
          <Flex between style={{ alignItems: 'center', marginBottom: '0.25rem' }}>
            <div>
              <Heading3 weight='700' style={{ margin: 0, fontSize: 18 }}>Daily Utilization</Heading3>
              <OtherText grey style={{ margin: '4px 0 0' }}>% of operating hours used on productive work</OtherText>
            </div>
            <TargetChip>Target 75%</TargetChip>
          </Flex>
          <ChartBox>
            <ChartBars>
              {utilBars.map((h, i) => (
                <Bar key={i} h={h} color={theme.colorOrange} muted={h < 60} />
              ))}
            </ChartBars>
            <ChartFooter>
              <OtherText grey style={{ margin: 0 }}>30d</OtherText>
              <OtherText grey style={{ margin: 0 }}>today</OtherText>
            </ChartFooter>
          </ChartBox>
        </Panel>

        <Panel>
          <Flex between style={{ alignItems: 'center', marginBottom: '0.25rem' }}>
            <div>
              <Heading3 weight='700' style={{ margin: 0, fontSize: 18 }}>Daily Operating Hours</Heading3>
              <OtherText grey style={{ margin: '4px 0 0' }}>Hours the machine was producing work per day</OtherText>
            </div>
            <TargetChip color={theme.colorGreen}>Avg 10.4h/day</TargetChip>
          </Flex>
          <ChartBox>
            <ChartBars>
              {opHoursBars.map((h, i) => (
                <Bar key={i} h={h * 6} color={theme.colorGreen} muted={h < 10} />
              ))}
            </ChartBars>
            <ChartFooter>
              <OtherText grey style={{ margin: 0 }}>30d</OtherText>
              <OtherText grey style={{ margin: 0 }}>today</OtherText>
            </ChartFooter>
          </ChartBox>
        </Panel>
      </Split>

      <Panel>
        <Heading3 weight='700' style={{ margin: 0, fontSize: 18 }}>Operator Performance on this Machine</Heading3>
        <OtherText grey style={{ margin: '4px 0 1rem' }}>How each operator performs when running this machine · universal metrics</OtherText>
        <OpTable>
          <thead>
            <tr>
              <th>Operator</th>
              <th>Shifts</th>
              <th>Op Hrs</th>
              <th>Idle Hrs</th>
              <th>Utilization</th>
              <th>Idle%</th>
            </tr>
          </thead>
          <tbody>
            {operators.map((o) => (
              <tr key={o.name}>
                <td>
                  <div className='op-cell'>
                    <span className='avatar'>{o.name.split(' ').map((s) => s[0]).join('')}</span>
                    <div>
                      <div>{o.name}</div>
                      {o.incident && <div className='incident'>1 incident</div>}
                    </div>
                  </div>
                </td>
                <td>{o.shifts}</td>
                <td>{o.op}</td>
                <td>{o.idle}</td>
                <td style={{
                  color: o.util >= 80 ? theme.colorGreen : o.util >= 70 ? theme.colorOrange : theme.colorRed,
                  fontWeight: 700,
                }}>
                  {o.util}%
                </td>
                <td style={{
                  color: o.idlePct <= 10 ? theme.colorGreen : o.idlePct <= 15 ? theme.colorOrange : theme.colorRed,
                  fontWeight: 700,
                }}>
                  {o.idlePct}%
                </td>
              </tr>
            ))}
          </tbody>
        </OpTable>

        <SignalBox>
          <BodyText style={{ margin: 0 }}>
            <span className='label'>Signal Layer observation:</span> Sarah Chen operates this machine at{' '}
            <span className='hl-green'>2.9% higher utilization</span> and{' '}
            <span className='hl-green'>10.7% better fuel efficiency per operating hour</span> than the machine average.
          </BodyText>
        </SignalBox>

        <ScrollIndicator>
          <ScrollFill w={22} />
        </ScrollIndicator>
      </Panel>

      <Split cols='1fr 1fr'>
        <Panel>
          <Heading3 weight='700' style={{ margin: 0, fontSize: 18 }}>Downtime Breakdown</Heading3>
          <OtherText grey style={{ margin: '4px 0 1rem' }}>Total: 42.3h · derived from status log</OtherText>
          <StackedBar>
            {downtime.map((d) => (
              <StackedSeg key={d.name} w={d.pct} color={d.color} />
            ))}
          </StackedBar>
          <LegendList>
            {downtime.map((d) => (
              <div className='li' key={d.name}>
                <div className='name'>
                  <span className='dot' style={{ background: d.color }} />
                  {d.name}
                </div>
                <OtherText grey style={{ margin: 0 }}>{d.hrs}</OtherText>
                <span className='pct'>{d.pct}%</span>
              </div>
            ))}
          </LegendList>
        </Panel>

        <Panel>
          <Heading3 weight='700' style={{ margin: 0, fontSize: 18 }}>vs Fleet Benchmark</Heading3>
          <OtherText grey style={{ margin: '4px 0 1rem' }}>How this machine compares to its category average</OtherText>
          <BenchList>
            {bench.map((b) => (
              <BenchRow key={b.k} positive={b.positive}>
                <BodyText grey style={{ margin: 0 }}>{b.k}</BodyText>
                <span className='v'>
                  <span className='num'>{b.num}</span>
                  <OtherText grey style={{ margin: 0 }}>{b.vs}</OtherText>
                  <span className='delta'>{b.delta}</span>
                </span>
              </BenchRow>
            ))}
          </BenchList>
        </Panel>
      </Split>

      <FuelMetricsPanel>
        <FuelTitleRow>
          <span className='icon'>
            <FuelIconSvg />
          </span>
          <Heading3 weight='700' style={{ margin: 0, fontSize: 18, flex: 1 }}>Fuel Metrics</Heading3>
          <span className='src'>⎌ Source: Fuel Module</span>
        </FuelTitleRow>
        <FuelMetricGrid>
          <FuelMetricItem>
            <ParagraphText grey weight='600' style={labelStyle}>Consumption</ParagraphText>
            <div className='value'>15.0k L</div>
            <OtherText grey style={{ margin: '4px 0 0' }}>28 dispenses</OtherText>
          </FuelMetricItem>
          <FuelMetricItem>
            <ParagraphText grey weight='600' style={labelStyle}>Avg Rate</ParagraphText>
            <div className='value orange'>47.9 L/h</div>
            <OtherText style={{ margin: '4px 0 0', color: theme.colorGreen }}>↗ -2.3 per op-hour</OtherText>
          </FuelMetricItem>
          <FuelMetricItem>
            <ParagraphText grey weight='600' style={labelStyle}>Efficiency</ParagraphText>
            <div className='value'>22.1 L</div>
            <OtherText grey style={{ margin: '4px 0 0' }}>per 100 tonnes</OtherText>
          </FuelMetricItem>
          <FuelMetricItem>
            <ParagraphText grey weight='600' style={labelStyle}>Fuel Cost</ParagraphText>
            <div className='value orange'>$24.0k</div>
            <OtherText grey style={{ margin: '4px 0 0' }}>$1.6/L avg</OtherText>
          </FuelMetricItem>
          <FuelMetricItem>
            <ParagraphText grey weight='600' style={labelStyle}>Variance</ParagraphText>
            <div className='value'>-18L</div>
            <OtherText grey style={{ margin: '4px 0 0' }}>vs expected</OtherText>
          </FuelMetricItem>
          <FuelMetricItem>
            <ParagraphText grey weight='600' style={labelStyle}>Status</ParagraphText>
            <div className='value green'>Verified</div>
            <OtherText grey style={{ margin: '4px 0 0' }}>1 anomaly flagged</OtherText>
          </FuelMetricItem>
        </FuelMetricGrid>
        <BoundaryNote>
          <BodyText style={{ margin: 0 }}>
            <span className='strong'>Boundary note.</span> These metrics are <span className='strong'>read from the Fuel Module</span> – Machinery does not compute or reconcile fuel.
          </BodyText>
        </BoundaryNote>
      </FuelMetricsPanel>

      <Panel>
        <Heading3 weight='700' style={{ margin: 0, fontSize: 18 }}>Cost Composition (30d)</Heading3>
        <OtherText grey style={{ margin: '4px 0 1rem' }}>
          Total: <span style={{ color: theme.colorOrange, fontWeight: 700 }}>$58,240</span> · rolled up from Fuel Module · Maintenance WOs · HRDL hours · Depreciation schedule
        </OtherText>
        {[
          { name: 'Fuel', src: 'src: Fuel Module', amt: '$23,968', pct: 41, color: theme.colorOrange },
          { name: 'Maintenance & Parts', src: 'src: Maintenance WOs', amt: '$15,840', pct: 27, color: fleetColors.yellow },
          { name: 'Operator Wages', src: 'src: HRDL', amt: '$11,950', pct: 21, color: fleetColors.info },
          { name: 'Depreciation', src: 'src: Finance schedule', amt: '$6,482', pct: 11, color: fleetColors.purple },
        ].map((c) => (
          <CostBarStacked key={c.name}>
            <div className='top'>
              <span>
                <BodyText weight='600' style={{ margin: 0, display: 'inline' }}>{c.name}</BodyText>
                <OtherText grey style={{ margin: 0, display: 'inline', marginLeft: 6 }}>{c.src}</OtherText>
              </span>
              <span className='amt'>
                {c.amt}
                <span className='pct'>{c.pct}%</span>
              </span>
            </div>
            <CostTrack>
              <CostFill w={c.pct * 2} color={c.color} />
            </CostTrack>
          </CostBarStacked>
        ))}

        <CostEfficiencyBox>
          <Heading3 weight='700' style={{ margin: '0 0 1rem', fontSize: 15 }}>Cost efficiency</Heading3>
          {[
            { k: 'Cost/Op-hour', sub: 'vs $203 fleet', v: '$186' },
            { k: 'Cost/Tonne', sub: 'vs $0.92 fleet', v: '$0.86' },
            { k: 'Cost/Cycle', sub: 'vs $82 fleet', v: '$78' },
            { k: 'Annualised', sub: 'vs -$700k fleet avg', v: '$699k' },
          ].map((r) => (
            <div className='ce-row' key={r.k}>
              <div>
                <BodyText style={{ margin: 0 }}>{r.k}</BodyText>
                <OtherText grey style={{ margin: '2px 0 0' }}>{r.sub}</OtherText>
              </div>
              <span className='ce-v'>{r.v}</span>
            </div>
          ))}
        </CostEfficiencyBox>
      </Panel>
    </>
  )
}
